/**
 * Goals Management Page (Story 18.8 — /goals)
 *
 * Tenant-wide goals surface, restructured to mirror the clients-list shell
 * (Epic 18 grid arm): the same page container (p-6 max-w-[1800px] mx-auto),
 * the same ReportBreadcrumb header, the same GoalsFilterToolbar (search +
 * filter dropdown + ViewSwitcher + Create button), and the same grid/table
 * view switch (GoalCardGrid ⇄ GoalsTable). The Active/History toggle is a
 * Show-Archived-style flip button (GoalsViewToggle) inside the toolbar's right
 * cluster; the History view renders its own copy to switch back.
 *
 * Reads GET /api/goals (the 18.8 list endpoint, enriched with clientName +
 * derived progress). All filtering/sorting is client-side (demo-scale row
 * counts; no server query params, no pagination — the exit-lens scope guard).
 *
 * The Create button is plan-gated (Story 18.10): it runs through
 * useUpgradeGuard({ feature: "goalLimit" }) with a count derived from the
 * loaded goals (free = tenant-total non-demo). Free users at the 2-goal limit
 * see the UpgradeLimitModal instead of the dialog; paid users open the dialog
 * and are checked per-client at submit (inside GoalFormDialog). While the
 * goals/clients queries load, the button is disabled (AC#10).
 */

import { useMemo, useState, useEffect, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@provenleap/ui";
import { Target, MagnifyingGlass, Trash } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { apiGet } from "../../../lib/apiClient";
import { usePermission, Actions } from "../../../hooks/usePermission";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import { useUpgradeGuard } from "../../../hooks/useUpgradeGuard";
import { useDebounce } from "../../../hooks/useDebounce";
import { countGoalsForGate } from "../../../lib/goalPlanGate";
import type { Client } from "../../../components/clients/types";
import { PageSpinner } from "../../../components/PageSpinner";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import {
  useGoals,
  useSetGoalStatus,
  useDeleteGoal,
} from "../../../hooks/useGoals";
import {
  filterGoals,
  sortGoals,
  GOAL_STATUS_VALUES,
  GOAL_PERIOD_TYPES,
  type GoalListItem,
  type GoalStatus,
  type GoalPeriodType,
  type GoalSortKey,
  type SortDirection,
} from "../../../lib/goalPageDisplay";
import { GoalsTable } from "../../../components/goals/GoalsTable";
import { GoalCardGrid } from "../../../components/goals/GoalCardGrid";
import { GoalDetailDialog } from "../../../components/goals/GoalDetailDialog";
import {
  GoalsFilterToolbar,
  type GoalClientOption,
} from "../../../components/goals/GoalsFilterToolbar";
import { GoalHistory } from "../../../components/goals/GoalHistory";
import { GoalsViewToggle } from "../../../components/goals/GoalsViewToggle";
import type { ClientViewMode } from "../../../components/clients/ViewSwitcher";
import { GoalFormDialog } from "../../../components/clients/GoalFormDialog";
import { GOAL_HIDEABLE_COLUMNS } from "../../../components/goals/goalColumns";
import { useHiddenColumns } from "../../../components/shared/columnVisibility";

const VALID_SORT_KEYS: readonly GoalSortKey[] = [
  "name",
  "status",
  "progress",
  "client",
  "platform",
  "metric",
  "interval",
];
const VALID_SORT_DIRS: readonly SortDirection[] = ["asc", "desc"];

/** The two page views (Story 18.7 adds "history" alongside 18.8's active table). */
export type GoalsView = "active" | "history";
const VALID_VIEWS: readonly GoalsView[] = ["active", "history"];

export interface GoalsSearch {
  q?: string;
  client?: string;
  status?: GoalStatus;
  interval?: GoalPeriodType;
  metric?: string;
  sort?: GoalSortKey;
  dir?: SortDirection;
  view?: GoalsView;
}

export const Route = createFileRoute("/_authenticated/goals/")({
  validateSearch: (search: Record<string, unknown>): GoalsSearch => ({
    q: typeof search.q === "string" && search.q !== "" ? search.q : undefined,
    client: typeof search.client === "string" ? search.client : undefined,
    status: GOAL_STATUS_VALUES.includes(search.status as GoalStatus)
      ? (search.status as GoalStatus)
      : undefined,
    interval: GOAL_PERIOD_TYPES.includes(search.interval as GoalPeriodType)
      ? (search.interval as GoalPeriodType)
      : undefined,
    metric: typeof search.metric === "string" ? search.metric : undefined,
    sort: VALID_SORT_KEYS.includes(search.sort as GoalSortKey)
      ? (search.sort as GoalSortKey)
      : undefined,
    dir: VALID_SORT_DIRS.includes(search.dir as SortDirection)
      ? (search.dir as SortDirection)
      : undefined,
    view: VALID_VIEWS.includes(search.view as GoalsView)
      ? (search.view as GoalsView)
      : undefined,
  }),
  component: GoalsPage,
});

function GoalsPage() {
  const navigate = useNavigate();
  const { q: urlSearch, client, status, interval, metric, sort, dir, view } = Route.useSearch();
  const activeView: GoalsView = view ?? "active";
  const { hasPermission } = usePermission();
  const canManage = hasPermission(Actions.MANAGE_CLIENTS);

  const { data: goals, isLoading, error, refetch } = useGoals();
  const setStatus = useSetGoalStatus();
  const deleteGoal = useDeleteGoal();
  const { plan } = usePlanLimits();

  // Story 18.10 — the clients list carries `demoMode`, the gate's demo-exclusion
  // input (the goals list alone does not). Reuses the clients page's ["clients"]
  // query (cache-shared). Drives both the page-level free-scope count and the
  // dialog's per-client submit gate.
  const { data: clients } = useQuery<Client[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? "Failed to load clients");
      }
      const json = await res.json();
      return json.data as Client[];
    },
  });

  // Page-level count: free = tenant-total non-demo goals; paid = 0 here (the
  // real per-client check happens at dialog submit, AC#8). `undefined` while
  // goals/clients load → the guard refuses + the button disables (AC#10).
  const pageGoalCount = countGoalsForGate(goals, clients, plan);
  const { guardAction, UpgradeModal } = useUpgradeGuard({
    feature: "goalLimit",
    currentCount: pageGoalCount,
  });

  const [viewGoal, setViewGoal] = useState<GoalListItem | null>(null);
  const [lastViewGoal, setLastViewGoal] = useState<GoalListItem | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editGoal, setEditGoal] = useState<GoalListItem | null>(null);
  const [lastEditGoal, setLastEditGoal] = useState<GoalListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GoalListItem | null>(null);

  // ── View mode (grid ⇄ table) — defaults to grid like the clients page ──
  const [viewMode, setViewMode] = useState<ClientViewMode>(
    () => (localStorage.getItem("goalsList_viewMode") as ClientViewMode) ?? "grid",
  );
  useEffect(() => {
    localStorage.setItem("goalsList_viewMode", viewMode);
  }, [viewMode]);

  // Hidden table columns — per-device, persisted to localStorage. Shared by the
  // table (hide buttons) and the toolbar's Columns menu (restore).
  const { hiddenColumns, toggleColumn } = useHiddenColumns(
    "goalsTable_hiddenColumns",
    GOAL_HIDEABLE_COLUMNS,
  );

  // ── Search state — debounced, URL-synced (mirrors the clients page) ──
  const [searchInput, setSearchInput] = useState(urlSearch ?? "");
  const debouncedSearch = useDebounce(searchInput, 300);
  useEffect(() => {
    setSearchInput(urlSearch ?? "");
  }, [urlSearch]);

  // Linger the edit goal ~300ms so the dialog exit animation can play (§4.6).
  useEffect(() => {
    if (editGoal) {
      setLastEditGoal(editGoal);
      return;
    }
    if (!lastEditGoal) return;
    const t = setTimeout(() => setLastEditGoal(null), 300);
    return () => clearTimeout(t);
  }, [editGoal, lastEditGoal]);

  // Same linger for the detail pop-up (GoalDetailDialog requires a non-null
  // goal, so we keep the last one mounted ~300ms for its exit animation, §4.6).
  useEffect(() => {
    if (viewGoal) {
      setLastViewGoal(viewGoal);
      return;
    }
    if (!lastViewGoal) return;
    const t = setTimeout(() => setLastViewGoal(null), 300);
    return () => clearTimeout(t);
  }, [viewGoal, lastViewGoal]);

  const effectiveSort: GoalSortKey = sort ?? "name";
  const effectiveDir: SortDirection = dir ?? "asc";

  // FILTER-toolbar client options: only clients that already HAVE goals — you
  // can't filter by a client with nothing to show. Derived from the goals list.
  const clientOptions: GoalClientOption[] = useMemo(() => {
    if (!goals) return [];
    const map = new Map<string, string>();
    for (const g of goals) map.set(g.clientId, g.clientName);
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [goals]);

  // CREATE/EDIT-dialog client options: the FULL client list (from the clients
  // query), NOT the goals-derived list above. Sourcing the picker from existing
  // goals made it impossible to create the FIRST goal for any client (empty
  // picker when no goals exist yet).
  const dialogClientOptions: GoalClientOption[] = useMemo(
    () =>
      (clients ?? [])
        .map((c) => ({ id: c.id, name: c.name ?? "" }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [clients],
  );

  // Platform-keyed metric options derived from the loaded goals.
  const metricOptions: string[] = useMemo(() => {
    if (!goals) return [];
    const set = new Set<string>();
    for (const g of goals) {
      if (g.platform) set.add(g.platform);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [goals]);

  const filtered = useMemo(() => {
    if (!goals) return [];
    let f = filterGoals(goals, { client, status, interval, metric });
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      f = f.filter((g) => g.name.toLowerCase().includes(q));
    }
    return sortGoals(f, effectiveSort, effectiveDir);
  }, [goals, client, status, interval, metric, debouncedSearch, effectiveSort, effectiveDir]);

  // ── URL-sync handlers ──────────────────────────────────────────────
  const setSearch = useCallback(
    (next: Partial<GoalsSearch>) => {
      navigate({
        from: Route.fullPath,
        search: (prev) => ({ ...prev, ...next }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      setSearch({ q: value || undefined });
    },
    [setSearch],
  );

  const handleSort = useCallback(
    (key: GoalSortKey) => {
      if (key === effectiveSort) {
        setSearch({ sort: key, dir: effectiveDir === "asc" ? "desc" : "asc" });
      } else {
        setSearch({ sort: key, dir: "asc" });
      }
    },
    [effectiveSort, effectiveDir, setSearch],
  );

  const clearFilters = useCallback(() => {
    setSearchInput("");
    setSearch({
      q: undefined,
      client: undefined,
      status: undefined,
      interval: undefined,
      metric: undefined,
    });
  }, [setSearch]);

  // ── Mutations ──────────────────────────────────────────────────────
  function handleTogglePause(goal: GoalListItem) {
    setStatus.mutate({
      clientId: goal.clientId,
      goalId: goal.id,
      status: goal.status === "paused" ? "on_track" : "paused",
    });
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteGoal.mutate(
      { clientId: deleteTarget.clientId, goalId: deleteTarget.id },
      { onSuccess: () => setDeleteTarget(null) },
    );
  }

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      <ReportBreadcrumb items={[{ label: "Goals" }]} />
      <div className="mb-6" />

      {/* History view (Story 18.7, AC #6) — independent of the active list's
          load state; scoped by the page's client filter. The Active/History
          toggle normally lives in GoalsFilterToolbar (active view only), so the
          History view renders its own right-aligned copy to switch back. */}
      {activeView === "history" && (
        <>
          <div className="mb-6 flex justify-end">
            <GoalsViewToggle
              activeView={activeView}
              onViewChange={(v) => setSearch({ view: v === "active" ? undefined : v })}
            />
          </div>
          <GoalHistory clientId={client} />
        </>
      )}

      {activeView === "active" && (
        <>
          {/* Loading spinner — matches the clients page */}
          {isLoading && (
            <div data-testid="goals-loading">
              <PageSpinner />
            </div>
          )}

          {/* Error banner — matches the clients page (with a Retry) */}
          {error && (
            <div
              className="rounded-md border border-destructive bg-destructive/5 p-4 text-sm text-destructive flex items-center justify-between"
              role="alert"
            >
              <span>{error.message}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="rounded-2xl border-[0.5px] border-input text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
              >
                Retry
              </Button>
            </div>
          )}

          {/* Empty state (no goals at all) — icon-in-muted-circle (clients style) */}
          {!isLoading && !error && goals && goals.length === 0 && (
            <div
              className="flex flex-col items-center justify-center py-16 text-center"
              data-testid="goals-empty"
            >
              <div className="rounded-full bg-muted p-4 mb-4">
                <Target size={32} className="text-muted-foreground" />
              </div>
              <h2 className="text-lg font-semibold mb-2">No goals yet</h2>
              <p className="text-muted-foreground mb-6 max-w-sm">
                Create your first goal to start tracking progress.
              </p>
              {canManage && (
                <Button
                  variant="brand"
                  className="rounded-2xl text-[13px] cursor-pointer"
                  onClick={() => guardAction(() => setCreateOpen(true))}
                  disabled={pageGoalCount === undefined}
                  data-testid="create-goal-empty-btn"
                >
                  <Target size={16} className="mr-1.5" />
                  Create your first goal
                </Button>
              )}
            </div>
          )}

          {/* Populated */}
          {!isLoading && !error && goals && goals.length > 0 && (
            <>
              <GoalsFilterToolbar
                search={searchInput}
                onSearchChange={handleSearchChange}
                client={client}
                onClientChange={(c) => setSearch({ client: c })}
                clientOptions={clientOptions}
                status={status}
                onStatusChange={(s) => setSearch({ status: s })}
                interval={interval}
                onIntervalChange={(i) => setSearch({ interval: i })}
                metric={metric}
                onMetricChange={(m) => setSearch({ metric: m })}
                metricOptions={metricOptions}
                onClearFilters={clearFilters}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                activeView={activeView}
                onViewChange={(v) => setSearch({ view: v === "active" ? undefined : v })}
                canManage={canManage}
                onCreateGoal={() => guardAction(() => setCreateOpen(true))}
                createDisabled={pageGoalCount === undefined}
                hiddenColumns={hiddenColumns}
                onToggleColumn={toggleColumn}
                showColumnsControl={viewMode === "table"}
              />

              {/* No-results state (goals exist but filters match none) */}
              {filtered.length === 0 ? (
                <div className="text-center py-12" data-testid="goals-no-results">
                  <MagnifyingGlass size={48} className="mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No goals match your filters</h3>
                  <p className="mt-1" style={{ color: "#5A7396" }}>
                    Try adjusting your filters.
                  </p>
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="mt-4 rounded-2xl border-[0.5px] border-input text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
                  >
                    Clear all filters
                  </Button>
                </div>
              ) : (
                <AnimatePresence mode="wait">
                  {viewMode === "grid" ? (
                    <motion.div
                      key="grid"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                      <GoalCardGrid
                        goals={filtered}
                        canManage={canManage}
                        onGoalClick={(g) => setViewGoal(g)}
                        onEdit={(g) => setEditGoal(g)}
                        onTogglePause={handleTogglePause}
                        onDelete={(g) => setDeleteTarget(g)}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="table"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                      <GoalsTable
                        goals={filtered}
                        canManage={canManage}
                        onView={(g) => setViewGoal(g)}
                        sortKey={effectiveSort}
                        sortDir={effectiveDir}
                        onSort={handleSort}
                        onEdit={(g) => setEditGoal(g)}
                        onTogglePause={handleTogglePause}
                        onDelete={(g) => setDeleteTarget(g)}
                        hiddenColumns={hiddenColumns}
                        onHideColumn={toggleColumn}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </>
          )}
        </>
      )}

      {/* Goal detail pop-up (§4.1 centered modal) — opened by clicking a row or
          card. Lingers the goal ~300ms so the exit animation plays (§4.6). */}
      {lastViewGoal && (
        <GoalDetailDialog
          goal={lastViewGoal}
          open={!!viewGoal}
          onOpenChange={(o) => !o && setViewGoal(null)}
          canManage={canManage}
          onEdit={(g) => setEditGoal(g)}
          onTogglePause={handleTogglePause}
          onDelete={(g) => setDeleteTarget(g)}
        />
      )}

      {/* Story 18.10 — free-tier upgrade prompt for the page-level Create
          button (paid-tier per-client prompt lives inside GoalFormDialog). */}
      {UpgradeModal}

      {/* Create dialog — always mounted (§4.6). Carries the gate inputs so a
          paid tier's per-client submit check (AC#8) and the 403 fallback
          (AC#9) can run inside the dialog. */}
      {canManage && (
        <GoalFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          clients={dialogClientOptions}
          goalsForGate={goals}
          clientsForGate={clients}
        />
      )}

      {/* Edit dialog — lingers the goal so exit animation plays (§4.6) */}
      {lastEditGoal && (
        <GoalFormDialog
          open={!!editGoal}
          onOpenChange={(o) => !o && setEditGoal(null)}
          clients={dialogClientOptions}
          goal={lastEditGoal}
        />
      )}

      {/* Delete confirmation (§4.4) */}
      <DeleteGoalDialog
        goal={deleteTarget}
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isPending={deleteGoal.isPending}
      />
    </div>
  );
}

// ============================================================================
// Delete confirmation dialog (§4.4 — same scaffold, 2-button footer)
// ============================================================================

interface DeleteGoalDialogProps {
  goal: GoalListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending: boolean;
}

function DeleteGoalDialog({
  goal,
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: DeleteGoalDialogProps) {
  function handleOpenChange(next: boolean) {
    if (!next && isPending) return;
    onOpenChange(next);
  }
  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-[60] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-3 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Delete goal?
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {goal
                      ? `"${goal.name}" and its history will be removed.`
                      : "Its history will be removed."}
                  </DialogPrimitive.Description>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant="outline"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: "0.5px solid var(--hairline)" }}
                    onClick={() => handleOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    onClick={onConfirm}
                    disabled={isPending}
                    data-testid="confirm-delete-goal-btn"
                  >
                    <Trash size={16} className="mr-1" />
                    {isPending ? "Deleting…" : "Delete"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
